import { useEffect, useRef } from 'react';
import { Coins, Plus, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCredits } from '@/hooks/useCredits';
import { CreditPurchase } from './CreditPurchase';
import { CreditHistory } from './CreditHistory';
import { cn } from '@/lib/utils';

interface CreditDisplayProps {
  showDetails?: boolean;
  className?: string;
  showAddButton?: boolean;
}

export const CreditDisplay = ({ showDetails = false, className = "", showAddButton = true }: CreditDisplayProps) => {
  const { credits, loading } = useCredits();
  const prevCreditsRef = useRef<number>(credits);
  const badgeRef = useRef<HTMLSpanElement>(null);

  // Animate badge when credits change
  useEffect(() => {
    if (prevCreditsRef.current !== credits && !loading) {
      badgeRef.current?.classList.remove('animate-credit-change');
      // Force reflow to restart animation
      void badgeRef.current?.offsetWidth;
      badgeRef.current?.classList.add('animate-credit-change');
      prevCreditsRef.current = credits;
    }
  }, [credits, loading]);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Coins className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">...</span>
      </div>
    );
  }

  const creditColor = credits <= 5 ? "destructive" : credits <= 10 ? "secondary" : "default";

  if (showDetails) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Coins className="h-5 w-5" />
              Mes Crédits
            </span>
            <span ref={badgeRef} className="inline-block">
              <Badge variant={creditColor} className="text-lg px-3 py-1">
                {credits}
              </Badge>
            </span>
          </CardTitle>
          <CardDescription>
            Utilisez vos crédits pour générer des œuvres d'art IA
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1">
                  <Plus className="h-4 w-4 mr-2" />
                  Acheter
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Acheter des Crédits</DialogTitle>
                  <DialogDescription>
                    Choisissez un pack de crédits pour continuer à générer des œuvres d'art
                  </DialogDescription>
                </DialogHeader>
                <Tabs defaultValue="packages" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="packages">Packs de Crédits</TabsTrigger>
                    <TabsTrigger value="history">Historique</TabsTrigger>
                  </TabsList>
                  <TabsContent value="packages">
                    <CreditPurchase />
                  </TabsContent>
                  <TabsContent value="history">
                    <CreditHistory />
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <History className="h-4 w-4 mr-2" />
                  Historique
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Historique des Crédits</DialogTitle>
                  <DialogDescription>
                    Consultez vos transactions récentes
                  </DialogDescription>
                </DialogHeader>
                <CreditHistory />
              </DialogContent>
            </Dialog>
          </div>

          {credits <= 5 && (
            <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
              <p className="text-sm text-destructive">
                Crédits faibles ! Pensez à acheter plus de crédits pour continuer à générer des oeuvres d'art.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <Coins className="h-4 w-4 text-muted-foreground" />
      <span ref={badgeRef} className="inline-block">
        <Badge variant={creditColor}>
          {credits}
        </Badge>
      </span>
      {showAddButton && (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-foreground/70 hover:bg-red-500/12 hover:text-red-600"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Acheter des Crédits</DialogTitle>
              <DialogDescription>
                Choisissez un pack de crédits pour continuer à générer des oeuvres d'art
              </DialogDescription>
            </DialogHeader>
            <CreditPurchase />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
